import React from 'react';
import { Box, Divider, useTheme } from '@mui/material';
import { Typography, Button, Icon } from '@fdha/web-ui-library';

import { getStyles } from './DeliveryFooter.styles';

interface DeliveryFooterProps {
  entreesSelected: number;
  entreesTotal: number;
  snacksSelected: number;
  snacksTotal: number;
  hasSides: boolean;
  isDirty: boolean;
  isSaving: boolean;
  isReadOnly: boolean;
  isCanceled?: boolean;
  isCancellable?: boolean;
  showingEmptyState?: boolean;
  onSave: () => void;
  onCancel: () => void;
  onReactivate?: () => void;
}

const DeliveryFooter: React.FC<DeliveryFooterProps> = ({
  entreesSelected,
  entreesTotal,
  snacksSelected,
  snacksTotal,
  hasSides,
  isDirty,
  isSaving,
  isReadOnly,
  isCanceled = false,
  isCancellable = true,
  showingEmptyState = false,
  onSave,
  onCancel,
  onReactivate,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const entreesLabel = 'Entree Dishes';
  const snacksLabel = 'Snacks/Sides'; // Always use "Snacks/Sides" to match Figma

  // Check if all selections are complete (like mobile)
  const allSelectionsMade =
    entreesSelected === entreesTotal && snacksSelected === snacksTotal;

  const renderCancelActionButtons = () => {
    if (!isCancellable) {
      return;
    }

    if (isCanceled && onReactivate) {
      return (
        <Button
          variant="text"
          onClick={onReactivate}
          disabled={isSaving}
          startIcon={
            <Icon
              name="alert-circle-outline"
              size="medium"
              fill={theme.palette.primary.main}
            />
          }
          sx={styles.cancelButton}
        >
          Reactivate delivery
        </Button>
      );
    }

    return (
      <Button
        variant="text"
        onClick={onCancel}
        disabled={isSaving}
        startIcon={
          <Icon
            name="alert-circle-outline"
            size="medium"
            fill={theme.palette.primary.main}
          />
        }
        sx={styles.cancelButton}
      >
        Cancel delivery
      </Button>
    );
  };

  return (
    <>
      {!showingEmptyState && <Divider sx={styles.divider} />}
      <Box sx={styles.container}>
        <Typography variant="body2">
          {allSelectionsMade ? (
            <Box component="span" sx={styles.completionMessage}>
              You have selected all options
            </Box>
          ) : (
            <>
              <Box component="span" sx={styles.summaryNumber}>
                {entreesSelected}/{entreesTotal}
              </Box>{' '}
              {entreesLabel},{' '}
              <Box component="span" sx={styles.summaryNumber}>
                {snacksSelected}/{snacksTotal}
              </Box>{' '}
              {snacksLabel}
            </>
          )}
        </Typography>

        <Box sx={styles.buttonGroup}>
          {renderCancelActionButtons()}
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isReadOnly || !isDirty || isSaving}
            sx={styles.saveButton}
          >
            {isSaving ? 'Saving...' : 'Save selection'}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default DeliveryFooter;
